import { useState } from 'react';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import { Card } from './ui/card';
import { Quote, ChevronLeft, ChevronRight, Building2, MapPin } from 'lucide-react';

// PLACEHOLDER TESTIMONIALS - Update with real client testimonials
const clientTestimonials = [
  {
    role: 'Director of Nursing',
    facility: 'Regional Medical Center',
    location: 'Phoenix, AZ',
    quote: 'Digitive Health has been instrumental in helping us maintain optimal staffing levels during our busiest seasons. Their travel nurses integrate seamlessly with our team, and the quality of candidates they provide is consistently exceptional. We\'ve reduced our time-to-fill by 40% since partnering with them.',
    image: 'https://static.prod-images.emergentagent.com/jobs/ab78dc37-82fa-4174-9e3d-2db23ac43aeb/images/d86aa60f094cc9eaecc35f36a05a077e96704673aadce5e58e4d7e560ef0f0a6.png',
  },
  {
    role: 'Chief Operations Officer',
    facility: 'Healthcare System Network',
    location: 'Dallas, TX',
    quote: 'What sets Digitive Health apart is their understanding of our unique needs. They don\'t just fill positions – they find professionals who align with our culture and values. Their managed services program has streamlined our entire workforce management process.',
    image: 'https://static.prod-images.emergentagent.com/jobs/ab78dc37-82fa-4174-9e3d-2db23ac43aeb/images/578a9619d033c3a0b1bc27a226560c350c0f074afe5ed98a7aee3cfd754907e3.png',
  },
  {
    role: 'HR Manager',
    facility: 'Hospital Network',
    location: 'San Francisco, CA',
    quote: 'During a critical staffing shortage, Digitive Health delivered qualified professionals within 48 hours. Their crisis response team understood the urgency and went above and beyond. They\'re not just a staffing agency – they\'re a true partner in patient care.',
    image: 'https://static.prod-images.emergentagent.com/jobs/ab78dc37-82fa-4174-9e3d-2db23ac43aeb/images/c1504a1be3973387ec7fbeeaecf96bf5378ff806aecf4d67501aafd49050d89a.png',
  },
  {
    role: 'Administrator',
    facility: 'Regional Health Partners',
    location: 'Chicago, IL',
    quote: 'We\'ve worked with many staffing agencies over the years, but Digitive Health stands out for their professionalism and reliability. Their direct hire placements have an impressive retention rate, and their team is always responsive to our evolving needs.',
    image: 'https://static.prod-images.emergentagent.com/jobs/ab78dc37-82fa-4174-9e3d-2db23ac43aeb/images/43c17e88b680eb92c607f4f16ac6b313c72e8ce09e7c272f018c0214345636b9.png',
  },
  {
    role: 'Vice President of Clinical Services',
    facility: 'Home Care Network',
    location: 'Miami, FL',
    quote: 'Digitive Health has transformed how we approach staffing. Their telehealth support team helped us scale our virtual care program rapidly, and their allied health professionals consistently exceed expectations. They truly understand the healthcare landscape.',
    image: 'https://static.prod-images.emergentagent.com/jobs/ab78dc37-82fa-4174-9e3d-2db23ac43aeb/images/d59836e8ae7a081aa8f8091f67b9725433edd662f514adefbe9f576f02ee23cd.png',
  },
];

export const ClientTestimonials = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [titleRef, titleVisible] = useScrollAnimation();
  const [contentRef, contentVisible] = useScrollAnimation();

  const nextTestimonial = () => {
    setActiveIndex((prev) => (prev + 1) % clientTestimonials.length);
  };

  const prevTestimonial = () => {
    setActiveIndex((prev) => (prev - 1 + clientTestimonials.length) % clientTestimonials.length);
  };

  const activeTestimonial = clientTestimonials[activeIndex];

  return (
    <section
      id="client-testimonials"
      data-testid="client-testimonials-section"
      className="section-padding bg-white"
    >
      <div className="container-width">
        {/* Section Header */}
        <div
          ref={titleRef}
          className={`text-center mb-12 animate-on-scroll ${titleVisible ? 'visible' : ''}`}
        >
          <span className="inline-block text-sm font-medium tracking-widest uppercase text-primary/60 mb-4">
            Client Success Stories
          </span>
          <h2 className="text-4xl md:text-5xl font-semibold tracking-tight text-primary">
            What Our Clients Say
          </h2>
        </div>

        {/* Testimonial Content */}
        <div
          ref={contentRef}
          className={`animate-on-scroll ${contentVisible ? 'visible' : ''}`}
        >
          <Card className="overflow-hidden bg-gradient-to-br from-primary/5 to-primary/10 border-primary/20 shadow-xl max-w-5xl mx-auto" data-testid="client-testimonial-card">
            <div className="p-8 md:p-12">
              {/* Facility Image */}
              <div className="flex justify-center mb-6">
                <div className="w-48 h-32 md:w-64 md:h-40 rounded-xl overflow-hidden ring-4 ring-primary/20 ring-offset-4 shadow-lg">
                  <img
                    src={activeTestimonial.image}
                    alt={activeTestimonial.facility}
                    className="w-full h-full object-cover"
                    data-testid="client-testimonial-photo"
                  />
                </div>
              </div>

              {/* Facility Badge */}
              <div className="flex justify-center mb-6">
                <div className="inline-flex items-center gap-3 px-5 py-2.5 bg-primary/10 rounded-full">
                  <Building2 className="w-5 h-5 text-primary" />
                  <span className="text-base font-semibold text-primary">{activeTestimonial.facility}</span>
                  <span className="text-muted-foreground">|</span>
                  <MapPin className="w-4 h-4 text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">{activeTestimonial.location}</span>
                </div>
              </div>

              {/* Quote */}
              <div className="text-center max-w-3xl mx-auto">
                <Quote className="h-8 w-8 text-primary/30 mx-auto mb-4" />
                <p className="text-lg md:text-xl text-foreground leading-relaxed mb-6">
                  "{activeTestimonial.quote}"
                </p>
                
                {/* Title Only */}
                <div className="border-t border-primary/20 pt-6 inline-block px-8">
                  <p className="text-lg font-semibold text-primary">
                    — {activeTestimonial.role}
                  </p>
                </div>
              </div>
            </div>
          </Card>

          {/* Navigation */}
          <div className="mt-8 flex flex-col items-center gap-6">
            {/* Thumbnail Row */}
            <div className="flex items-center justify-center gap-3">
              {clientTestimonials.map((testimonial, index) => (
                <button
                  key={index}
                  onClick={() => setActiveIndex(index)}
                  className={`relative overflow-hidden transition-all duration-300 rounded-lg ${
                    index === activeIndex 
                      ? 'w-20 h-14 md:w-24 md:h-16 ring-4 ring-primary ring-offset-2 shadow-lg' 
                      : 'w-14 h-10 md:w-16 md:h-12 opacity-50 hover:opacity-100 grayscale hover:grayscale-0'
                  }`}
                  data-testid={`client-testimonial-thumb-${index}`}
                >
                  <img
                    src={testimonial.image}
                    alt={testimonial.facility}
                    className="w-full h-full object-cover"
                  />
                </button>
              ))}
            </div>

            {/* Arrow Navigation */}
            <div className="flex items-center gap-4">
              <button
                onClick={prevTestimonial}
                className="w-12 h-12 rounded-full bg-white border border-border flex items-center justify-center hover:bg-primary hover:text-white hover:border-primary transition-colors shadow-sm"
                data-testid="client-testimonial-prev"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <span className="text-sm text-muted-foreground min-w-[60px] text-center font-medium">
                {activeIndex + 1} / {clientTestimonials.length}
              </span>
              <button
                onClick={nextTestimonial}
                className="w-12 h-12 rounded-full bg-white border border-border flex items-center justify-center hover:bg-primary hover:text-white hover:border-primary transition-colors shadow-sm"
                data-testid="client-testimonial-next"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ClientTestimonials;
